import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { camelize, camelizeKeys, decamelizeKeys, HumpsProcessorParameter } from "humps";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isObject from "lodash/isObject";
import startsWith from "lodash/startsWith";
import { Session } from "PFApp/use_session";
import { decamelizeDesiredKeys } from "PFCore/helpers/keys_camelization_utils";
import QueryString from "qs";

export const DEFAULT_API_VERSION = 2;
const META_SNAKE_CASE_KEYS = ["meta.filters", "meta.defaultFilters"];

const setAuthorizationHeader = (config: InternalAxiosRequestConfig, token: string | null): void => {
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
};

export const clearInterceptors = (instance: AxiosInstance): void => {
  instance.interceptors.request.clear();
  instance.interceptors.response.clear();
};

export const setVersionedUrl = <T extends { url?: string; apiVersion?: number; skipVersioning?: boolean }>(
  config: T
): void => {
  const apiVersion = config.apiVersion ?? DEFAULT_API_VERSION;

  delete config.apiVersion;

  const { skipVersioning } = config;

  delete config.skipVersioning;

  if (config.url && !skipVersioning && !config.url.startsWith("http")) {
    config.url = `v${apiVersion}/${config.url.replace(/^\/+/, "")}`;
  }
};

export const setHeaders = (
  config: InternalAxiosRequestConfig,
  token: string | null,
  fullDomain?: string
): void => {
  if (fullDomain) {
    config.headers.ACCOUNT = fullDomain;
  }
  setAuthorizationHeader(config, token);

  if (!isNil(window.PF.config.locale)) {
    config.headers["Accept-Language"] = window.PF.config.locale;
  }
};

export const cleanRequestParams = (config: InternalAxiosRequestConfig): void => {
  const requestData = config.params || config.data || {};

  if (requestData.order && isEmpty(requestData.order)) {
    delete requestData.order;
  }
};

export const parseRequestToSnakeCase = (config: InternalAxiosRequestConfig): void => {
  if (config.params) {
    config.params = decamelizeKeys(config.params);
  }

  if (config.data) {
    config.data = decamelizeKeys(config.data);
  }
};

export const camelizeApiKey = (value: string, convert: HumpsProcessorParameter = camelize) =>
  `${startsWith(value, "_") ? "_" : ""}${convert(value)}`;

export const camelizeApiKeys = <T>(data: T) => camelizeKeys(data, camelizeApiKey);

export const parseResponseToCamelCase = (response: AxiosResponse): void => {
  const contentType = response.headers["content-type"] as string;

  if (isObject(response.data) && contentType?.includes("application/json")) {
    response.data = camelizeApiKeys(response.data);

    if ("meta" in response.data) {
      response.data = decamelizeDesiredKeys(response.data, META_SNAKE_CASE_KEYS);
    }
  }
};

export const addPaginationParamsToResponse = (response: AxiosResponse) => {
  const {
    config: { params, data: requestDataString },
    data
  } = response;
  const isFormData = requestDataString instanceof FormData;
  const requestData = requestDataString && !isFormData ? JSON.parse(requestDataString) : {};
  const perPage = params?.per_page ?? requestData.per_page;
  const page = params?.page ?? requestData.page;

  if (!!data.meta && perPage !== undefined && page !== undefined && !Number.isNaN(data.meta?.total)) {
    const totalPages = Math.max(Math.ceil(data.meta.total / perPage), 1);
    response.data = {
      ...data,
      meta: {
        ...data?.meta,
        perPage,
        page,
        totalPages
      }
    };
  }
};

export const refreshTokenAndRetry = (
  session: Session,
  originalRequest?: InternalAxiosRequestConfig
): Promise<AxiosResponse> =>
  new Promise((resolve, reject) => {
    session
      .refreshToken()
      .then(() => {
        if (originalRequest) {
          setAuthorizationHeader(originalRequest, session.getAccessToken());

          axios(originalRequest)
            .then((res) => {
              parseResponseToCamelCase(res);
              addPaginationParamsToResponse(res);
              resolve(res);
            })
            .catch(() => {
              session.expireSession();
            });
        }
      })
      .catch(reject);
  });

export const setParamsSerializer = (config: InternalAxiosRequestConfig) => {
  config.paramsSerializer = {
    serialize: (params) =>
      QueryString.stringify(params, {
        arrayFormat: "brackets",
        skipNulls: true
      })
  };
};
